<template>
  <BaseAdvancedTable
    @delete:cell="handleDelete"
    @delete:bulk="handleBulkDelete"
    @search="onSearch"
    @paginate="onPaginate"
    @update:selection-list="handleUpdateSelectionList"
    :actions="tableActions"
    :fields="fields"
    :items="items"
    :busy="isBusy"
    :sort-desc="false"
    :pagination-options="[5, 10, 25, 50, 100]"
    :page-size="pagination.perPage"
    :total-rows="pagination.total"
    sort-by="age"
    server-side
    ref="tableElementRef"
  >
    <template #table(head-left)>
      <div class="d-flex align-items-center" style="margin-right: auto">
        <b-button
          v-if="!isBroker"
          :disabled="isChagingView"
          variant="link"
          @click="handleToggleViewApproved"
          class="py-1 pl-2 pr-1 light-text"
          >{{ toggleApproveViewButtonText }}</b-button
        >
        <b-button
          v-if="!isBroker && tableSelectionList.length > 0"
          :disabled="isApproving || isBusy"
          variant="link"
          @click="handleApprove"
          class="py-1 pl-2 light-text"
          >{{ approveButtonText }}</b-button
        >
      </div>
    </template>
  </BaseAdvancedTable>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  reactive,
  ref,
  onMounted,
  computed,
  watch
} from '@vue/composition-api'

import { useUserCompany } from '@composables'
import { OrphanClaims } from '@services'

import { default as fields } from './utils/tableFields'
import axios from 'axios'

export default defineComponent({
  name: 'OrphanClaimsTable',
  setup() {
    const { roleName: companyRoleName, roleValue: companyRoleValue, isRoleName } =
      useUserCompany()

    const isBroker = computed(() => isRoleName('Broker'))
    const tableActions = computed(() => (isBroker.value ? [] : ['delete']))

    const isBusy = ref(true)
    const items = ref([])
    const tableSelectionList = ref([])
    const isViewingApproved = ref(false)
    const isApproving = ref(false)
    const isChagingView = ref(false)
    const tableElementRef = ref()
    const abortController = ref()

    const fetchItems = async () => {
      try {
        isBusy.value = true
        if (abortController.value) {
          abortController.value.abort()
        }
        abortController.value = new AbortController()
        const {
          data: {
            data: { count, results }
          }
        } = await OrphanClaims.getOrphanClaims(
          companyRoleName.value,
          companyRoleValue.value,
          {
            search: search.value,
            page: pagination.currentPage,
            page_size: pagination.perPage,
            approved: isViewingApproved.value
          },
          abortController.value.signal
        )

        items.value = results
        pagination.total = count
        isBusy.value = false
      } catch (error) {
        if (!axios.isCancel(error) && error.code !== 'ERR_CANCELED') {
          isBusy.value = false
          throw error
        }
      }
    }

    const handleDelete = async (data) => {
      if (isBroker.value) return

      const index = items.value.findIndex((item) => data.id === item.id)

      await OrphanClaims.deleteOrphanClaims([data.id]).catch((err) =>
        Promise.reject(err)
      )

      items.value.splice(index, 1)

      Vue.toasted.show('Orphan claim deleted successfully.')
    }
    const removeItemsById = (ids) => {
      const filtered = items.value.filter(({ id }) => !ids.includes(id))
      items.value = filtered
    }

    const handleBulkDelete = async (datas) => {
      if (isBroker.value) return

      const ids = datas.map((data) => data.id)

      await OrphanClaims.deleteOrphanClaims(ids).catch((err) =>
        Promise.reject(err)
      )
      removeItemsById(ids)
      Vue.toasted.show('Orphan claims deleted successfully.')
    }

    onMounted(fetchItems)

    const pagination = reactive({
      currentPage: 1,
      perPage: 100,
      total: 1
    })
    const onPaginate = (page) => {
      pagination.currentPage = page

      fetchItems()
    }

    const search = ref(null)
    const onSearch = (keyword) => {
      search.value = keyword
      fetchItems()
    }
    const handleToggleViewApproved = async () => {
      if (isBroker.value) return

      try {
        isChagingView.value = true
        pagination.currentPage = 1
        isViewingApproved.value = !isViewingApproved.value
        await fetchItems()
        tableElementRef.value.clearSelectionList()
      } finally {
        isChagingView.value = false
      }
    }
    const handleApprove = async () => {
      if (isBroker.value) return

      const ids = tableSelectionList.value
      try {
        isApproving.value = true
        isBusy.value = true
        const response = await OrphanClaims.approveOphanClaim(
          !isViewingApproved.value,
          ids
        )
        removeItemsById(ids)
        tableElementRef.value.clearSelectionList()
        Vue.toasted.show(response.data.message)
      } finally {
        isApproving.value = false
        isBusy.value = false
      }
    }
    const handleUpdateSelectionList = (list) => {
      tableSelectionList.value = list
    }
    const approveButtonText = computed(() => {
      if (isApproving.value) {
        return 'Processing Items'
      } else if (isViewingApproved.value) {
        return 'Unapprove'
      } else {
        return 'Approve'
      }
    })
    const toggleApproveViewButtonText = computed(() => {
      if (isChagingView.value) {
        return 'Loading View'
      } else if (isViewingApproved.value) {
        return 'View Unapproved'
      } else {
        return 'View Approved'
      }
    })
    return {
      isBusy,
      isBroker,
      tableActions,
      fields,
      items,
      handleDelete,
      handleBulkDelete,
      handleApprove,
      handleToggleViewApproved,
      handleUpdateSelectionList,
      tableSelectionList,
      onSearch,
      pagination,
      onPaginate,
      approveButtonText,
      toggleApproveViewButtonText,
      tableElementRef,
      isApproving,
      isChagingView
    }
  }
})
</script>

<style scoped>
>>> .navbar-search {
  margin-left: 0 !important;
}
>>> .bulk-delete-btn {
  margin-right: 16px !important;
}
</style>
